import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '@center-pwa/store/store';
import {
  SpaceRendererOrganism,
  SpaceBusProvider,
  useSpaceWires,
  useApiClientsOrNull,
  LoaderMolecule,
  MetaspaceToolbar,
  ToolbarStateProvider,
  ToolbarProvider,
  ArmillaryProvider,
  useMediaQuery,
  EditSphereLauncherMolecule,
  useEditSphereLauncher,
  useCompositionResolver,
} from 'design-system/my-lib';
import type { ToolbarItem } from 'design-system/my-lib';
import type { SpaceDocument, SpaceValidationError } from 'types/space';
import { createLegacyEventBridge } from './legacyEventBridge';
import './SpaceHost.scss';

interface SpaceHostProps {
  /**
   * Must be REFERENTIALLY STABLE across renders. `useCompositionResolver` derives `isResolving`
   * from document identity, so a document computed inline in a parent's render body (e.g.
   * `metaspaceToSpace(...)` rather than a memo or a store selector) would flip the host back to the
   * loader before paint on every parent render and unmount the entire surface each time.
   */
  document: SpaceDocument;
  metaspaceId: string;
  sphereId: string;
  toolbarOpen?: boolean;
  onToolbarOpenChange?: (isOpen: boolean) => void;
  toolbarActiveItem?: ToolbarItem;
  onToolbarActiveItemChange?: (item: ToolbarItem) => void;
}

/**
 * Mounts a `space/v1` document via the host-agnostic `SpaceRendererOrganism`, scoping wire
 * delivery to this Space instance (`SpaceBusProvider`) and bridging the closed legacy-event subset
 * onto the existing global bus so toolbar/chat consumers keep working (SC6). Prefetches any
 * composition refs the document contains (`useCompositionResolver`) so a referencing Space never
 * flashes an unknown-component placeholder for a composition that would otherwise resolve.
 *
 * Reproduces the metaspace-level chrome `MetaspaceGrid` owns (toolbar + its providers) — the
 * Surface itself only renders the grid/section content, so this host, not the renderer, is where
 * that chrome lives. Deliberate duplication of the gating expression below while dual-read lives;
 * see the twin comment in `MetaspaceGrid.tsx`.
 */
const SpaceHostInner: React.FC<SpaceHostProps> = ({
  document,
  metaspaceId,
  sphereId,
  toolbarOpen = false,
  onToolbarOpenChange,
  toolbarActiveItem = 'chatbox',
  onToolbarActiveItemChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  // Read through a ref so `env` (and every organism it re-renders) does not change on each navigation.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const onNavigate = useCallback((path: string) => navigateRef.current(path), []);
  const { t } = useTranslation();
  // The identity-scoped organisms (Profile, Preferences, Security, TaskList) read these from
  // `ctx.env`; the legacy `MetaspaceGrid` read the same slices straight from the store.
  const organizationId = useSelector((state: RootState) => state.userProfile?.userSphere?.properties?.sphereId) || undefined;
  const identityId = useSelector((state: RootState) => state.userProfile?.identity?.identity?.id) || undefined;
  const userId = useSelector((state: RootState) => state.userProfile?.userId) || undefined;
  // Which sphere the toolbar's chat panel is pointed at — set by a chat affordance inside the
  // rendered organisms, exactly as `MetaspaceGrid.tsx:225-229` does for the legacy branch.
  const [chatSphereId, setChatSphereId] = useState<string | undefined>(undefined);
  const apiClients = useApiClientsOrNull();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const onLegacyBridge = useMemo(
    () => createLegacyEventBridge(dispatch, { sphereId, metaspaceId }),
    [dispatch, sphereId, metaspaceId]
  );
  const onAction = useSpaceWires({ wires: document.wires, onLegacyBridge });
  const { resolveComposition, isResolving } = useCompositionResolver(document, apiClients?.schemaClient);
  const {
    onEditSphere,
    fetchPermissionList,
    sphereToEdit,
    visible: editSphereDialogVisible,
    onSave: onEditSphereSave,
    onSphereRefresh: onEditSphereRefresh,
    onClose: onEditSphereClose,
    sphereChange,
  } = useEditSphereLauncher();
  const onExpansionErrors = useCallback((errors: SpaceValidationError[]) => {
    // eslint-disable-next-line no-console
    console.error('[SpaceHost] composition expansion error', errors);
  }, []);

  const handleChatBubbleClick = useCallback(
    (clickedSphereId: string) => {
      setChatSphereId(clickedSphereId);
      onToolbarActiveItemChange?.('chatbox');
      onToolbarOpenChange?.(true);
    },
    [onToolbarActiveItemChange, onToolbarOpenChange]
  );

  // Mirrors `MetaspaceGrid.tsx`'s `hasAtlasOrganism`/`hasArmillaryOrganism`/`showToolbar` gate
  // (spec § Phase R3 task 1) — the document carries the same organism vocabulary via A2UI
  // component entries rather than `layout[]` entries, so presence is derived from `a2ui.components`
  // instead. Conversion (`metaspaceToSpace`) preserves the `settings` carried by both branches.
  const settings = document.settings;
  const hasAtlasOrganism = document.a2ui.components.some((entry) => entry.component === 'Atlas');
  const hasArmillaryOrganism = document.a2ui.components.some((entry) => entry.component === 'ArmillaryView');
  const showToolbar = (hasAtlasOrganism || hasArmillaryOrganism) && settings?.showMetasphereToolbar !== false;
  const needsArmillaryContext = hasArmillaryOrganism;

  const toolbarDefaultOpen = settings?.toolbarDefaultOpen ?? false;
  const toolbarDefaultActiveItem = settings?.toolbarDefaultActiveItem as ToolbarItem | undefined;
  const enabledToolbarItems = settings?.enabledToolbarItems as ToolbarItem[] | undefined;
  const disabledToolbarItems = settings?.disabledToolbarItems;
  const chatboxContextMode = settings?.toolbarItemSettings?.chatbox?.contextMode;

  // Memoized: `CompositionHost` hands `env` down to every adapter, so a fresh object literal per
  // render (and `chatSphereId` state alone causes one) re-renders the whole organism tree.
  const env = useMemo(
    () => ({
      sphereId,
      organizationId,
      identityId,
      userId,
      onNavigate,
      toolbarOpen,
      showToolbar,
      onChatBubbleClick: handleChatBubbleClick,
      onEditSphere,
      sphereChange,
      fetchPermissionList,
    }),
    [sphereId, organizationId, identityId, userId, onNavigate, toolbarOpen, showToolbar, handleChatBubbleClick, onEditSphere, sphereChange, fetchPermissionList]
  );

  // `isResolving` is true on the FIRST render for a document with composition refs (it is derived,
  // not flipped by an effect), so the organisms below mount exactly once — after resolution —
  // rather than mounting, unmounting and remounting around the loader.
  if (isResolving) {
    return (
      <div className="space-host space-host--resolving">
        <LoaderMolecule variant="inline" caption={t('space.render.resolvingCompositions')} />
      </div>
    );
  }

  const content = (
    <ToolbarStateProvider initialExpanded={toolbarOpen}>
      <div className="space-host">
        {showToolbar && !isMobile && (
          <MetaspaceToolbar
            onOpenChange={onToolbarOpenChange}
            items={enabledToolbarItems}
            disabledItems={disabledToolbarItems}
            defaultOpen={toolbarDefaultOpen}
            defaultActiveItem={toolbarDefaultActiveItem}
            activeSphereId={sphereId}
            chatSphereId={chatSphereId}
            chatboxContextMode={chatboxContextMode}
            onEditSphere={onEditSphere}
          />
        )}
        <div className="space-host__surface" role="region" aria-label={t('space.render.surfaceRegion')}>
          <SpaceRendererOrganism
            document={document}
            env={env}
            resolveComposition={resolveComposition}
            onAction={onAction}
            onExpansionErrors={onExpansionErrors}
          />
        </div>
      </div>
      <EditSphereLauncherMolecule
        sphereToEdit={sphereToEdit}
        visible={editSphereDialogVisible}
        onSave={onEditSphereSave}
        onSphereRefresh={onEditSphereRefresh}
        onClose={onEditSphereClose}
        fetchPermissionList={fetchPermissionList}
      />
    </ToolbarStateProvider>
  );

  const withToolbar = showToolbar ? (
    <ToolbarProvider initialSphereId={sphereId} sphereChange={sphereChange}>
      {content}
    </ToolbarProvider>
  ) : (
    content
  );

  if (needsArmillaryContext) {
    return <ArmillaryProvider initialRootSphereId={sphereId}>{withToolbar}</ArmillaryProvider>;
  }

  return withToolbar;
};

const SpaceHost: React.FC<SpaceHostProps> = (props) => {
  // One instance id per mount — never regenerated across re-renders of the same Space so wire
  // delivery stays scoped to this instance for its whole lifetime (SC6).
  const instanceIdRef = useRef<string>(`space-${props.metaspaceId}-${props.sphereId}`);

  return (
    <SpaceBusProvider instanceId={instanceIdRef.current}>
      <SpaceHostInner {...props} />
    </SpaceBusProvider>
  );
};

export default SpaceHost;
